import { useMutation } from '@apollo/client'
import { Alert, SecondaryButton } from '@designsforhealth/dfh-react-components'
import { CircularProgress } from '@material-ui/core'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import {
  AddSimpleProductsToCartDocument,
  AddSimpleProductsToCartMutation,
} from '../../../../../graphql/magento'
import useUserCategory from '../../../../../hooks/useUserCategory'
import { TokenContext } from '../../../../../layouts/context'
import { publishAddToCartEventForItems } from '../../../../../lib/analytics/cart'
import { useCartContext } from '../../../../../lib/cart'
import { useAddToCartToast } from '../../../../../lib/cart/hooks/useAddToCartToast'
import { resolveItemQuantityConfig } from '../../../../../lib/cart/item-control-options'
import { useDispensaryCartStatus } from '../../../../../lib/dispensaries/events/cart'
import { isMagentoProductsQueryConfiguredSimpleProduct } from '../../../../../lib/products'
import { logAndCaptureException } from '../../../../../utils/errorTools'
import { tapSuccessfulQueryResponse } from '../../../../../utils/graphqlTools'
import {
  maxItemOrderQuantityByUserCategory,
  reorderItemQuantity,
} from '../../../../../utils/itemTools'
import Link from '../../../../global/Link'
import OrderContents from './OrderContents'
import { OrderDetails } from './OrderDetails'
import OrderInvoices from './OrderInvoices'
import { OrderItemEntryProps } from './OrderItemEntry'
import { OrderTotal } from './OrderTotal'
import { OrderPageData } from './types'

export const StyledActions = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: repeat(auto-fit, minmax(min-content, 172px));
  gap: 16px;
  justify-content: flex-start;
  margin-top: 20px;
  margin-bottom: 20px;
  ${SecondaryButton} {
    display: flex;
    // center text vertically if one button wraps before another
    align-items: center;
    justify-content: center;
  }
  @media print {
    display: none;
  }
`

export const StyledHeader = styled.h3`
  @media print {
    padding-bottom: 20px;
  }
`

export interface OrderPageContentProps {
  order: OrderPageData
  urlFragment: string
}

export interface CartItem {
  data: {
    sku: string
    quantity: number
    dfh_order_type: number
    dfh_autoship_interval: number
  }
}

function handlePrint(): void {
  if (typeof window === 'undefined') {
    return
  }
  window.print()
}

const OrderPageContent: React.VFC<OrderPageContentProps> = ({ order, urlFragment }) => {
  const { loading: dispensaryCartLoading } = useDispensaryCartStatus()
  const token = useContext(TokenContext)
  const { cart } = useCartContext()
  const { userCategory } = useUserCategory()
  const [outOfStockError, setOutOfStockError] = useState<string | undefined>(undefined)
  const [resolvedItems, setResolvedItems] = useState<OrderItemEntryProps['item'][]>([])
  const { showSuccessToast } = useAddToCartToast()
  const maxItemOrderQtyByUserCategory = maxItemOrderQuantityByUserCategory(userCategory)

  const { cartItems, skus } = useMemo(() => {
    return order.items.reduce(
      (acc: { cartItems: CartItem[]; skus: string[] }, item) => {
        if (item?.sku && item?.quantity != null) {
          let reorderQuantity = 0
          const orderItemWithProduct = resolvedItems
            .filter((orderItem) => isMagentoProductsQueryConfiguredSimpleProduct(orderItem.product))
            .find((product) => product.sku === item.sku)
          if (!orderItemWithProduct) {
            return acc
          }
          const reorderItemQty = reorderItemQuantity({
            minSaleQty: orderItemWithProduct.product.stock_configuration?.min_sale_qty,
            prevOrderQty: item.quantity,
          })
          const matchingItemInCart = cart?.items?.find(
            (itemInCart) => itemInCart?.product?.sku === item.sku,
          )
          const currentCartItemQuantity = matchingItemInCart?.quantity ?? 0
          const { maxQuantityAllowedInCart } = resolveItemQuantityConfig(
            matchingItemInCart?.product?.stock_configuration,
          )
          // Conditionally set reorderQuantity
          if (maxItemOrderQtyByUserCategory) {
            /*
            If the item configuration's max cart quantity is less than the default override value,
            honor the configuration's value instead.
            */
            const maxItemOrderQuantity =
              maxQuantityAllowedInCart >= maxItemOrderQtyByUserCategory
                ? maxItemOrderQtyByUserCategory
                : maxQuantityAllowedInCart
            reorderQuantity =
              maxItemOrderQuantity > currentCartItemQuantity + reorderItemQty
                ? reorderItemQty
                : maxItemOrderQuantity <= currentCartItemQuantity
                  ? 0
                  : maxItemOrderQuantity > currentCartItemQuantity
                    ? maxItemOrderQuantity - currentCartItemQuantity
                    : reorderItemQty
          } else {
            reorderQuantity = reorderItemQty
          }
          if (reorderQuantity !== 0) {
            acc.cartItems.push({
              data: {
                sku: item.sku,
                quantity: reorderQuantity,
                dfh_order_type: 1,
                dfh_autoship_interval: 1,
              },
            })
            if (!acc.skus.includes(item.sku)) {
              acc.skus.push(item.sku)
            }
          }
        }
        return acc
      },
      { cartItems: [], skus: [] },
    )
  }, [cart?.items, maxItemOrderQtyByUserCategory, order.items, resolvedItems])

  const onCompleted = useCallback(
    (mutationData: AddSimpleProductsToCartMutation) => {
      if (mutationData?.addSimpleProductsToCart) {
        showSuccessToast({ skus: skus })
      }
    },
    [showSuccessToast, skus],
  )

  const [addSimpleProductsToCart, { loading: addToCartLoading, error }] = useMutation(
    AddSimpleProductsToCartDocument,
    {
      context: { token },
      onCompleted,
    },
  )

  const handleReorderAll = useCallback(() => {
    const cartId = cart?.id
    if (!cartId) {
      return
    }
    if (cartItems.length > 0) {
      addSimpleProductsToCart({
        variables: {
          cartId,
          cartItems,
        },
      }).then(
        tapSuccessfulQueryResponse(
          publishAddToCartEventForItems({
            items: cartItems.map(({ data: { sku, quantity } }) => ({ sku, quantity })),
          }),
        ),
      )
    }
  }, [addSimpleProductsToCart, cart?.id, cartItems])

  useEffect(() => {
    if (error) {
      logAndCaptureException(error)
    }
    setOutOfStockError(
      error?.graphQLErrors.find((graphQLError) =>
        /Product that you are trying to add is not available/.test(graphQLError.message),
      )?.message,
    )
  }, [error])

  return (
    <div>
      <StyledHeader>Order #{order.orderNumber}</StyledHeader>
      <StyledActions>
        <SecondaryButton as={Link} to={`/account/${urlFragment}/${order.orderId}/shipments`}>
          Track This Order
        </SecondaryButton>
        <SecondaryButton
          onClick={handleReorderAll}
          disabled={addToCartLoading || dispensaryCartLoading || cartItems.length === 0}
        >
          {addToCartLoading ? <CircularProgress size={16} color="inherit" /> : 'Reorder All Items'}
        </SecondaryButton>
        <SecondaryButton onClick={handlePrint}>Print This Order</SecondaryButton>
      </StyledActions>
      {error && (
        <Alert type="error">{outOfStockError || 'Could not add product to shopping cart.'}</Alert>
      )}
      <div>
        <OrderDetails order={order} />
        <OrderContents
          order={order}
          reorderDisabled={addToCartLoading}
          resolvedItems={resolvedItems}
          setResolvedItems={setResolvedItems}
        />
        <OrderTotal order={order} />
        {order.__typename === 'CustomerOrder' && !!order.invoices?.length && (
          <>
            <hr />
            <OrderInvoices invoices={order.invoices} />
          </>
        )}
      </div>
    </div>
  )
}

export default OrderPageContent
